import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import type { components } from "../api/types";

export type APIToken = components["schemas"]["APIToken"];
export type APITokenCreated = components["schemas"]["APITokenCreated"];

const QUERY_KEY = ["me", "api-tokens"] as const;

// useApiTokensList fetches GET /api/v1/me/api-tokens. The response carries
// metadata only (token_id, name, created_at, last_used_*) — never secrets;
// the secret is only ever returned from the POST that minted it. A 401
// here means the cookie expired between the user reaching /settings and
// the panel rendering; RequireAuth will catch the next /me refetch.
export function useApiTokensList() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<APIToken[]> => {
      const { data, error } = await apiClient.GET("/api/v1/me/api-tokens");
      if (error) throw error;
      return data?.tokens ?? [];
    },
    staleTime: 30_000,
  });
}

// useCreateApiToken mints a new token. The server's response contains the
// one-time `secret` (`pmt_<hex>`); the caller's onSuccess pulls the secret
// out and stashes it in component state for the reveal panel. We invalidate
// the list query so the new token's metadata row appears immediately, but
// the secret itself is NEVER cached — it lives only in the caller's local
// React state until the user acknowledges and we wipe it.
export function useCreateApiToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string): Promise<APITokenCreated> => {
      const { data, error } = await apiClient.POST("/api/v1/me/api-tokens", {
        params: { header: { "X-Requested-With": "xhr" } },
        body: { name },
      });
      if (error) throw error;
      return data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

// useRevokeApiToken deletes a token by id. The orchestrator returns 204;
// any client still using the token gets 401 on the next request. We
// invalidate the list so the row disappears.
export function useRevokeApiToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tokenId: string): Promise<void> => {
      const { error } = await apiClient.DELETE("/api/v1/me/api-tokens/{tid}", {
        params: {
          path: { tid: tokenId },
          header: { "X-Requested-With": "xhr" },
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
